import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FiLock, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { API_URL } from '../../api/client';
import { Button } from '../../components/Button';
import { ErrorMessage } from '../../components/ErrorMessage';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. No token provided.');
      setVerifying(false);
      return;
    }

    // Verify token on mount
    const verifyToken = async () => {
      try {
        const response = await fetch(`${API_URL}/password-reset/verify/${token}`);
        const data = await response.json();

        if (response.ok) {
          setUserEmail(data.data.email);
          setVerifying(false);
        } else {
          setError(data.message || 'Invalid or expired reset link.');
          setVerifying(false);
        }
      } catch (err) {
        setError('Failed to verify reset link. Please try again.');
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/password-reset/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(data.message || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-header" style={{ textAlign: 'center' }}>
              <div className="spinner" style={{ margin: '0 auto var(--space-4)' }}></div>
              <p className="auth-subtitle">Verifying reset link...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !token) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-header" style={{ textAlign: 'center' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', backgroundColor: 'var(--color-error-light)', borderRadius: '50%', marginBottom: 'var(--space-4)' }}>
                <FiAlertCircle style={{ width: '32px', height: '32px', color: 'var(--color-error)' }} />
              </div>
              <h2>Invalid Reset Link</h2>
              <p className="auth-subtitle" style={{ marginBottom: 'var(--space-6)' }}>{error}</p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Link to="/forgot-password">
                <Button variant="primary" size="lg">
                  Request New Link
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-header" style={{ textAlign: 'center' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', backgroundColor: 'var(--color-success-light)', borderRadius: '50%', marginBottom: 'var(--space-4)' }}>
                <FiCheckCircle style={{ width: '32px', height: '32px', color: 'var(--color-success)' }} />
              </div>
              <h2>Password Reset Successful!</h2>
              <p className="auth-subtitle" style={{ marginBottom: 'var(--space-4)' }}>
                Your password has been reset successfully. You can now login with your new password.
              </p>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-6)' }}>
                Redirecting to login page...
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Link to="/login">
                <Button variant="primary" size="lg">
                  Go to Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h2>Reset Password</h2>
            <p className="auth-subtitle">
              Enter your new password for <strong>{userEmail}</strong>
            </p>
          </div>

          {error && <ErrorMessage message={error} onClose={() => setError('')} />}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
                minLength={6}
                disabled={loading}
                autoComplete="new-password"
              />
              <p style={{ marginTop: 'var(--space-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                At least 6 characters
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                minLength={6}
                disabled={loading}
                autoComplete="new-password"
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={loading}
              disabled={loading}
            >
              Reset Password
            </Button>
          </form>

          <div className="auth-footer">
            <p className="auth-link-text">
              <Link to="/login" className="auth-link">
                Cancel and go back to login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
